import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Checkbox,
  CircularProgress,
  Skeleton,
} from "@mui/material";
import {
  Add,
  Search,
  Group,
  Campaign,
  People,
  Close,
  Settings,
  Login,
  Logout,
} from "@mui/icons-material";
import {
  fetchGroups,
  fetchCommunityGroups,
  fetchBroadcastGroups,
  createGroup,
  createCommunityGroup,
  createBroadcastGroup,
  joinCommunityGroup,
  leaveCommunityGroup,
} from "../../store/slices/chatSlice";
import api from "../../services/api";
import { usePermissions } from "../../hooks/usePermissions";

const GroupChat = () => {
  const { hasPermission } = usePermissions();
  const canCreateGroupChat = hasPermission("chat.group");
  const canCreateCommunityGroup =
    hasPermission("chat.group.create_class") ||
    hasPermission("chat.group.create_department") ||
    hasPermission("chat.group.create_institute");
  const canCreateBroadcastGroup =
    hasPermission("chat.broadcast.create_department") ||
    hasPermission("chat.broadcast.create_institute");
  const canCreateAnyGroup = canCreateGroupChat || canCreateCommunityGroup || canCreateBroadcastGroup;
  const dispatch = useDispatch();
  const {
    groups = [],
    communityGroups = [],
    broadcastGroups = [],
    loading,
  } = useSelector((state) => state.chat);

  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [groupType, setGroupType] = useState("community");
  const [formData, setFormData] = useState({ name: "", description: "", members: [] });
  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    dispatch(fetchGroups());
    dispatch(fetchCommunityGroups());
    dispatch(fetchBroadcastGroups());
  }, [dispatch]);

  const loadUsers = async () => {
    if (usersList.length > 0) return;
    setUsersLoading(true);
    try {
      const res = await api.get("/chat/users?limit=100");
      setUsersList(res.data?.data || []);
    } catch {
      setUsersList([]);
    }
    setUsersLoading(false);
  };

  const allGroups = [
    ...groups.map((g) => ({ ...g, type: g.type || "group" })),
    ...communityGroups.map((g) => ({ ...g, type: "community" })),
    ...broadcastGroups.map((g) => ({ ...g, type: "broadcast" })),
  ];

  const filteredGroups = allGroups.filter((group) => {
    const matchesSearch = (group.name || "").toLowerCase().includes(searchQuery.toLowerCase());
    if (activeTab === 0) return matchesSearch;
    if (activeTab === 1) return matchesSearch && group.type === "community";
    if (activeTab === 2) return matchesSearch && group.type === "broadcast";
    return matchesSearch;
  });

  const handleCreateGroup = async () => {
    if (!formData.name) return;
    if (groupType === "community" && !canCreateCommunityGroup) return;
    if (groupType === "broadcast" && !canCreateBroadcastGroup) return;
    if (groupType === "group" && !canCreateGroupChat) return;
    setCreating(true);
    try {
      if (groupType === "community") {
        await dispatch(createCommunityGroup({ name: formData.name, description: formData.description })).unwrap();
        dispatch(fetchCommunityGroups());
      } else if (groupType === "broadcast") {
        await dispatch(createBroadcastGroup({ name: formData.name, description: formData.description })).unwrap();
        dispatch(fetchBroadcastGroups());
      } else {
        await dispatch(createGroup({ name: formData.name, memberIds: formData.members })).unwrap();
        dispatch(fetchGroups());
      }
    } catch (err) {
      console.error("Create group failed:", err);
    }
    setCreating(false);
    setCreateDialogOpen(false);
    setFormData({ name: "", description: "", members: [] });
  };

  const handleMemberToggle = (userId) => {
    setFormData((prev) => ({
      ...prev,
      members: prev.members.includes(userId)
        ? prev.members.filter((id) => id !== userId)
        : [...prev.members, userId],
    }));
  };

  const handleJoinCommunity = async (groupId) => {
    await dispatch(joinCommunityGroup(groupId));
    dispatch(fetchCommunityGroups());
  };

  const handleLeaveCommunity = async (groupId) => {
    await dispatch(leaveCommunityGroup(groupId));
    dispatch(fetchCommunityGroups());
  };

  const openCreateDialog = () => {
    setCreateDialogOpen(true);
    loadUsers();
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Groups & Broadcasts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage community groups and broadcast channels
          </Typography>
        </Box>
        {canCreateAnyGroup ? (
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            Create Group
          </Button>
        ) : null}
      </Box>

      {/* Search and Tabs */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <TextField
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><Search /></InputAdornment>
                ),
              }}
            />
          </Box>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
            <Tab label="All" icon={<Group />} iconPosition="start" />
            <Tab label="Community Groups" icon={<People />} iconPosition="start" />
            <Tab label="Broadcast Channels" icon={<Campaign />} iconPosition="start" />
          </Tabs>
        </CardContent>
      </Card>

      {/* Groups Grid */}
      <Grid container spacing={3}>
        {loading ? (
          [...Array(6)].map((_, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Card sx={{ height: "100%" }}>
                <CardContent>
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={56} height={56} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="70%" />
                      <Skeleton variant="text" width="40%" height={20} />
                    </Box>
                  </Box>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          filteredGroups.map((group) => (
            <Grid item xs={12} sm={6} md={4} key={`${group.type}-${group.id}`}>
              <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}>
                    <Avatar
                      src={group.image || undefined}
                      sx={{
                        bgcolor: group.type === "broadcast" ? "warning.main" : "primary.main",
                        width: 56,
                        height: 56,
                      }}
                    >
                      {group.type === "broadcast" ? <Campaign /> : <Group />}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" fontWeight={600}>
                        {group.name}
                      </Typography>
                      <Chip
                        label={group.type === "broadcast" ? "Broadcast" : group.type === "community" ? "Community" : "Group"}
                        size="small"
                        color={group.type === "broadcast" ? "warning" : "primary"}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>

                  {group.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {group.description}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <People sx={{ fontSize: 18, color: "text.secondary" }} />
                      <Typography variant="body2" color="text.secondary">
                        {group.members || 0} members
                      </Typography>
                    </Box>
                    {group.createdBy && (
                      <Typography variant="caption" color="text.disabled">
                        Created by {group.createdBy}
                      </Typography>
                    )}
                  </Box>
                </CardContent>

                <Box sx={{ p: 2, pt: 0, display: "flex", gap: 1 }}>
                  {group.type === "community" && (
                    <>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ flex: 1 }}
                        startIcon={<Login />}
                        onClick={() => handleJoinCommunity(group.id)}
                      >
                        Join
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<Logout />}
                        onClick={() => handleLeaveCommunity(group.id)}
                      >
                        Leave
                      </Button>
                    </>
                  )}
                  {group.type !== "community" && (
                    <Button variant="outlined" size="small" sx={{ flex: 1 }}>
                      View
                    </Button>
                  )}
                  <IconButton size="small"><Settings /></IconButton>
                </Box>
              </Card>
            </Grid>
          ))
        )}

        {!loading && filteredGroups.length === 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 6 }}>
                <Group sx={{ fontSize: 64, color: "grey.300", mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No groups found
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  Try adjusting your search or create a new group
                </Typography>
                {canCreateAnyGroup ? (
                  <Button variant="outlined" startIcon={<Add />} sx={{ mt: 2 }} onClick={openCreateDialog}>
                    Create Group
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Create Group Dialog */}
      <Dialog open={createDialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setCreateDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            Create New Group
            <IconButton onClick={() => setCreateDialogOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Group Type</InputLabel>
                <Select value={groupType} label="Group Type" onChange={(e) => setGroupType(e.target.value)}>
                  {canCreateCommunityGroup && (
                  <MenuItem value="community">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><People /> Community Group</Box>
                  </MenuItem>
                  )}
                  {canCreateBroadcastGroup && (
                  <MenuItem value="broadcast">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Campaign /> Broadcast Channel</Box>
                  </MenuItem>
                  )}
                  {canCreateGroupChat && (
                  <MenuItem value="group">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Group /> Private Group</Box>
                  </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
            {groupType === "group" && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>Add Members</Typography>
                <List sx={{ maxHeight: 200, overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1 }}>
                  {usersLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : usersList.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                      No users available
                    </Typography>
                  ) : (
                    usersList.map((user) => (
                      <ListItem key={user.id} dense button onClick={() => handleMemberToggle(user.id)}>
                        <Checkbox
                          edge="start"
                          checked={formData.members.includes(user.id)}
                          tabIndex={-1}
                        />
                        <ListItemAvatar>
                          <Avatar src={user.profileImage || undefined} sx={{ width: 32, height: 32 }}>
                            {(user.fullName || user.firstName || "?")[0]}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={user.fullName || `${user.firstName || ""} ${user.lastName || ""}`.trim() || "User"}
                          secondary={user.role}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
                <Typography variant="caption" color="text.secondary">
                  {formData.members.length} members selected
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateGroup} disabled={!formData.name || creating}>
            {creating ? <CircularProgress size={20} /> : "Create Group"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GroupChat;
